import React, { useEffect, useRef, useState } from 'react'
import moment from 'moment'
import Alert from '@mui/material/Alert'
import Avatar from '@mui/material/Avatar'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import PublicIcon from '@mui/icons-material/Public'
import API from '../../../../services/API'
import CustomDataGrid from '../../../../components/DataGrid/CustomDataGrid'
import AmbuNetLogo from '../../../../assets/images/ambunetLogo.png'
import { StatTile } from '../DashboardCards'
import AccessLogFilters, { defaultAccessLogFilters } from './AccessLogFilters'
import {
  accessLogActor,
  accessLogRecord,
  accessLogSummary,
  kindMeta,
} from './accessLogPresentation'

const serialiseFilters = (filters, pagination) => ({
  search: filters.search,
  startDate: filters.startDate,
  endDate: filters.endDate,
  kinds: filters.kinds,
  externalOnly: filters.externalOnly,
  accessedBy: filters.accessedBy?._id ?? null,
  page: pagination.page,
  pageSize: pagination.pageSize,
})

// Every access across all patient care records, for Governance. Mounted on
// the Patient Care Records dashboard behind the "Access log" tab.
const AccessLogsBoard = ({ initialFilters }) => {
  const [filters, setFilters] = useState(() => ({
    ...defaultAccessLogFilters(),
    ...(initialFilters ?? {}),
  }))
  const filtersRef = useRef(filters)
  filtersRef.current = filters

  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [pagination, setPagination] = useState({ page: 0, pageSize: 25 })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [summary, setSummary] = useState(null)
  const [summaryLoading, setSummaryLoading] = useState(true)

  const load = (
    overrides = {},
    nextPagination = { page: 0, pageSize: pagination.pageSize },
  ) => {
    const merged = { ...filtersRef.current, ...overrides }
    setLoading(true)
    setError(null)
    setPagination(nextPagination)
    API.post(
      '/governance/epcr/access-logs',
      serialiseFilters(merged, nextPagination),
    )
      .then((res) => {
        if (res.status === 200) {
          setRows(res.data.logs ?? [])
          setTotal(res.data.total ?? 0)
        } else if (res.status === 403) {
          setError(
            'Your verified session has expired. Reload the page to sign in again.',
          )
        } else {
          setError('Unable to load the access log. Please try again.')
        }
      })
      .catch(() => setError('Unable to load the access log. Please try again.'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    API.get('/governance/epcr/access-logs/summary')
      .then((res) => res.status === 200 && setSummary(res.data))
      .finally(() => setSummaryLoading(false))
  }, [])

  // First load, and again whenever the dashboard hands over a new preset
  // (e.g. the "Being Shared" tile).
  useEffect(() => {
    const next = { ...defaultAccessLogFilters(), ...(initialFilters ?? {}) }
    setFilters(next)
    load(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilters])

  const applyQuick = (overrides) => {
    setFilters((prev) => ({ ...prev, ...overrides }))
    load(overrides)
  }

  const weekRange = () => ({
    startDate: moment().subtract(7, 'days').startOf('day'),
    endDate: moment().endOf('day'),
  })

  const openRecord = (log) => {
    const { href } = accessLogRecord(log)
    if (href) window.open(href, '_blank', 'noopener,noreferrer')
  }

  return (
    <Stack gap={2}>
      <Stack direction="row" gap={1.5} flexWrap="wrap">
        <StatTile
          label="Access Events"
          value={summary?.total}
          caption="All activity in the past 7 days"
          color="anMainBlue"
          loading={summaryLoading}
          onClick={() => applyQuick({ ...weekRange(), kinds: [], externalOnly: false })}
        />
        <StatTile
          label="Record Views"
          value={summary?.viewed}
          caption="Opened by staff in the past 7 days"
          color="anBlue"
          loading={summaryLoading}
          onClick={() => applyQuick({ ...weekRange(), kinds: ['viewed'], externalOnly: false })}
        />
        <StatTile
          label="PDF Downloads"
          value={summary?.downloaded}
          caption="By staff in the past 7 days"
          color="anPurple"
          loading={summaryLoading}
          onClick={() =>
            applyQuick({ ...weekRange(), kinds: ['downloaded'], externalOnly: false })
          }
        />
        <StatTile
          label="External Downloads"
          value={summary?.external}
          caption="Via share codes in the past 7 days"
          color="warning"
          loading={summaryLoading}
          onClick={() => applyQuick({ ...weekRange(), kinds: ['external'], externalOnly: false })}
        />
        <StatTile
          label="Active Share Codes"
          value={summary?.activeShareCodes}
          caption="Codes that can be used right now"
          color="success"
          loading={summaryLoading}
          onClick={() =>
            applyQuick({
              ...weekRange(),
              kinds: ['shareCreated', 'shareRevoked', 'external'],
              externalOnly: false,
            })
          }
        />
      </Stack>

      <AccessLogFilters
        filters={filters}
        setFilters={setFilters}
        onSearch={(overrides) => load(overrides ?? {})}
        loading={loading}
      />

      {error && <Alert severity="error">{error}</Alert>}

      <CustomDataGrid
        rows={rows}
        getRowId={(row) => row._id}
        loading={loading}
        maxHeight={600}
        rowHeight={58}
        hideFilters
        paginationMode="server"
        rowCount={total}
        paginationModel={pagination}
        onPaginationModelChange={(model) => load({}, model)}
        pageSizeOptions={[25, 50, 100]}
        disableColumnSorting
        disableRowSelectionOnClick
        onRowClick={({ row }) => openRecord(row)}
        documentTitle="Record access log"
        sx={{
          '.MuiDataGrid-cell:focus': { outline: 'none' },
          '& .MuiDataGrid-row:hover': { cursor: 'pointer' },
        }}
        columns={[
          {
            field: 'createdAt',
            headerName: 'When',
            minWidth: 160,
            valueGetter: (value, row) =>
              moment(row.createdAt).format('YYYY-MM-DD HH:mm'),
            renderCell: ({ row }) => {
              const date = moment(row.createdAt)
              return (
                <Stack sx={{ overflow: 'hidden' }}>
                  <Typography variant="body2" noWrap>
                    {date.format('ddd DD MMM YYYY')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {date.format('HH:mm')}
                  </Typography>
                </Stack>
              )
            },
          },
          {
            field: 'activity',
            headerName: 'Activity',
            minWidth: 260,
            flex: 1.2,
            valueGetter: (value, row) => accessLogSummary(row),
            renderCell: ({ row }) => {
              const meta = kindMeta(row.kind)
              return (
                <Stack sx={{ overflow: 'hidden' }} gap={0.25}>
                  <Chip
                    size="small"
                    icon={<meta.Icon />}
                    label={meta.label}
                    color={meta.color}
                    variant={meta.color === 'default' ? 'outlined' : 'filled'}
                    sx={{
                      borderRadius: 1,
                      fontWeight: 700,
                      height: 22,
                      alignSelf: 'flex-start',
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {accessLogSummary(row)}
                  </Typography>
                </Stack>
              )
            },
          },
          {
            field: 'who',
            headerName: 'Who',
            minWidth: 200,
            flex: 1,
            valueGetter: (value, row) => accessLogActor(row).name,
            renderCell: ({ row }) => {
              const actor = accessLogActor(row)
              return (
                <Stack direction="row" gap={1} alignItems="center" sx={{ overflow: 'hidden' }}>
                  <Avatar
                    src={actor.system ? AmbuNetLogo : actor.photograph}
                    sx={{ width: 28, height: 28, borderRadius: 1 }}
                  >
                    {actor.external ? <PublicIcon fontSize="small" /> : actor.name?.[0]}
                  </Avatar>
                  <Stack sx={{ overflow: 'hidden' }}>
                    <Typography variant="body2" noWrap>
                      {actor.name}
                    </Typography>
                    {actor.external && (
                      <Typography variant="caption" color="warning.main">
                        External · share code
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              )
            },
          },
          {
            field: 'record',
            headerName: 'Record',
            minWidth: 200,
            flex: 1,
            valueGetter: (value, row) => {
              const record = accessLogRecord(row)
              return [record.number, record.patient].filter(Boolean).join(' · ')
            },
            renderCell: ({ row }) => {
              const record = accessLogRecord(row)
              return (
                <Stack sx={{ overflow: 'hidden' }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {record.number || '—'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {[
                      record.patient,
                      record.date && moment(record.date).format('DD MMM YYYY'),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </Typography>
                </Stack>
              )
            },
          },
          {
            field: 'recordType',
            headerName: 'Type',
            minWidth: 110,
            align: 'center',
            headerAlign: 'center',
            valueGetter: (value, row) => accessLogRecord(row).type ?? '',
            renderCell: ({ row }) => {
              const { type } = accessLogRecord(row)
              return type ? (
                <Tooltip title="Open record in a new tab">
                  <Chip
                    size="small"
                    variant="outlined"
                    label={type}
                    sx={{ borderRadius: 1 }}
                  />
                </Tooltip>
              ) : (
                ''
              )
            },
          },
        ]}
      />
    </Stack>
  )
}

export default AccessLogsBoard
